/**
 * Centralized date/time formatting utilities for BhriguGPT frontend.
 *
 * All date/time display across the frontend should use these helpers
 * to ensure consistency. Standardized on 'en-IN' locale.
 *
 * DATE FORMATTING STANDARDS:
 * - Full date:          "22 Feb 2026"                  (formatDate)
 * - Date + time:        "22 Feb 2026, 9:38 am"         (formatDateTime)
 * - Short date:         "22 Feb"                        (formatDateShort)
 * - Full long date:     "22 February 2026"              (formatDateLong)
 * - Time only:          "9:38 am"                       (formatTime)
 * - Relative time:      "2 hours ago"                   (formatRelativeTime)
 * - Chart axis (daily): "22 Feb"                        (formatChartDateDaily)
 * - Chart axis (month): "Feb 2026"                      (formatChartDateMonthly)
 */

const LOCALE = 'en-IN';

export interface DateParts {
    year: number;
    month: number;
    day: number;
}

function parseDateInput(dateInput: string | Date | null | undefined): Date | null {
    if (!dateInput) return null;
    const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
    return isNaN(date.getTime()) ? null : date;
}

function isValidDateParts(parts: DateParts): boolean {
    const { year, month, day } = parts;
    if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) return false;
    if (year < 1 || month < 1 || month > 12 || day < 1) return false;

    const date = new Date(year, month - 1, day);
    return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
}

function pad2(value: number): string {
    return String(value).padStart(2, '0');
}

/**
 * Parse a Kundli DOB. Canonical format is strict ISO YYYY-MM-DD — both at
 * rest in Mongo and on the wire. Anything else is invalid input and returns
 * null (the prod migration converted all legacy DD/MM/YYYY rows on
 * 2026-05-07; new DOBs are normalized at the API boundary).
 */
export function parseKundliDob(dob: string | null | undefined): DateParts | null {
    const value = dob?.trim();
    if (!value) return null;

    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return null;

    const parts = {
        year: parseInt(match[1], 10),
        month: parseInt(match[2], 10),
        day: parseInt(match[3], 10),
    };
    return isValidDateParts(parts) ? parts : null;
}

export function normalizeKundliDobToISO(dob: string | null | undefined): string {
    return parseKundliDob(dob) ? dob!.trim() : '';
}

export function formatKundliDobForApi(dob: string | null | undefined): string | null {
    return parseKundliDob(dob) ? dob!.trim() : null;
}

/**
 * Medium display date: '22 Feb 2026'
 */
export function formatDate(dateInput: string | Date | null | undefined): string {
    const date = parseDateInput(dateInput);
    if (!date) return '';
    return date.toLocaleDateString(LOCALE, { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * Full date + time: '22 Feb 2026, 9:38 am'
 */
export function formatDateTime(dateInput: string | Date | null | undefined): string {
    const date = parseDateInput(dateInput);
    if (!date) return '';
    return `${formatDate(date)}, ${formatTime(date)}`;
}

/**
 * Short date (no year): '22 Feb'
 */
export function formatDateShort(dateInput: string | Date | null | undefined): string {
    const date = parseDateInput(dateInput);
    if (!date) return '';
    return date.toLocaleDateString(LOCALE, { day: 'numeric', month: 'short' });
}

/**
 * Long date: '22 February 2026'
 */
export function formatDateLong(dateInput: string | Date | null | undefined): string {
    const date = parseDateInput(dateInput);
    if (!date) return '';
    return date.toLocaleDateString(LOCALE, { day: 'numeric', month: 'long', year: 'numeric' });
}

/**
 * Time only: '9:38 am'
 */
export function formatTime(dateInput: string | Date | null | undefined): string {
    const date = parseDateInput(dateInput);
    if (!date) return '';
    return date.toLocaleTimeString(LOCALE, {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    }).toLowerCase();
}

/**
 * Relative time: '2 hours ago', '3 days ago', 'just now'
 */
export function formatRelativeTime(dateInput: string | Date | null | undefined): string {
    const date = parseDateInput(dateInput);
    if (!date) return '';

    const seconds = Math.floor((Date.now() - date.getTime()) / 1000);

    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;

    // Fallback to formatted date for older items
    return formatDate(date);
}

/**
 * Chart axis label for daily view: '22 Feb'
 */
export function formatChartDateDaily(dateInput: string | Date | null | undefined): string {
    const date = parseDateInput(dateInput);
    if (!date) return '';
    return date.toLocaleDateString(LOCALE, { day: 'numeric', month: 'short' });
}

/**
 * Chart axis label for monthly view: 'Feb 2026'
 */
export function formatChartDateMonthly(dateInput: string | Date | null | undefined): string {
    const date = parseDateInput(dateInput);
    if (!date) return '';
    return date.toLocaleDateString(LOCALE, { month: 'short', year: 'numeric' });
}

/**
 * Long display date with weekday: 'Sunday, 22 Feb 2026'
 */
export function formatDateWithWeekday(dateInput: string | Date | null | undefined): string {
    const date = parseDateInput(dateInput);
    if (!date) return '';
    return date.toLocaleDateString(LOCALE, {
        weekday: 'long',
        day: 'numeric',
        month: 'short',
        year: 'numeric',
    });
}

/**
 * Date input value in browser-local timezone: 'YYYY-MM-DD'
 */
export function getLocalISODate(dateInput?: string | Date): string {
    const date = parseDateInput(dateInput || new Date());
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
}

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * DOB display for kundli surfaces. Output is human-readable: '12 Nov 1996'.
 * Storage/wire stays canonical YYYY-MM-DD via normalizeKundliDobToISO and
 * formatKundliDobForApi — never call this when sending to the backend.
 */
export function formatDob(dob: string | null | undefined): string {
    const parts = parseKundliDob(dob);
    if (!parts) return dob || '';
    return `${pad2(parts.day)} ${MONTH_SHORT[parts.month - 1]} ${parts.year}`;
}
